// src/pages/customer/customerpanel/InquiriesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BASE_URL || 'http://localhost:4000'}${url}`;
};

const STATUS = {
  open:    { label: 'Open',    color: 'bg-amber-100 text-amber-700'     },
  replied: { label: 'Replied', color: 'bg-emerald-100 text-emerald-700' },
  closed:  { label: 'Closed',  color: 'bg-gray-100 text-gray-500'       },
};

function InquiryCard({ inquiry }) {
  const st      = STATUS[inquiry.status] || STATUS.open;
  const isMeat  = inquiry.type === 'meat';
  const image   = isMeat ? getImageUrl(inquiry.product_image) : getImageUrl(inquiry.animal_image);
  const itemName = isMeat ? inquiry.product_name : inquiry.animal_name;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 p-4 space-y-3
                    hover:border-primary/20 transition-all">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-surface-container flex-shrink-0">
          {image
            ? <img src={image} alt={itemName} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center">
                <span className="material-symbols-outlined text-outline text-[22px]">
                  {isMeat ? 'storefront' : 'pets'}
                </span>
              </div>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-sm text-on-surface">{itemName || '—'}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              isMeat ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'
            }`}>
              {isMeat ? '🥩 Meat' : '🐐 Animal'}
            </span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>
              {st.label}
            </span>
          </div>
          <p className="text-[11px] text-secondary mt-0.5">
            To: <span className="font-bold">{inquiry.seller_name || '—'}</span>
          </p>
          {inquiry.seller_phone && (
            <a href={`tel:${inquiry.seller_phone}`}
              className="text-[10px] text-primary flex items-center gap-0.5 hover:underline mt-0.5">
              <span className="material-symbols-outlined text-[12px]">call</span>
              {inquiry.seller_phone}
            </a>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
              Inquiry #{inquiry.id}
            </span>
            {inquiry.animal_id && (
              <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
                Animal #{inquiry.animal_id}
              </span>
            )}
            {inquiry.product_id && (
              <span className="text-[10px] font-mono text-outline bg-surface-container px-2 py-0.5 rounded-full">
                Product #{inquiry.product_id}
              </span>
            )}
            <span className="text-[10px] text-outline">
              {new Date(inquiry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' · '}
              {new Date(inquiry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* Message */}
      <div className="bg-surface-container-low rounded-xl p-3">
        <p className="text-[10px] font-black uppercase text-secondary mb-1">Your Message</p>
        <p className="text-sm text-on-surface">{inquiry.message}</p>
      </div>

      {/* Reply */}
      {inquiry.reply ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
          <p className="text-[10px] font-black uppercase text-emerald-700 mb-1">
            {isMeat ? "Butcher's Reply" : "Farmer's Reply"}
          </p>
          <p className="text-sm text-on-surface">{inquiry.reply}</p>
        </div>
      ) : (
        <p className="text-xs text-secondary italic flex items-center gap-1">
          <span className="material-symbols-outlined text-[14px]">hourglass_empty</span>
          Waiting for reply...
        </p>
      )}
    </div>
  );
}

export default function InquiriesPanel() {
  const [inquiries, setInquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('all');

  const fetchInquiries = useCallback(() => {
    setLoading(true);
    api.get('/api/customer/inquiries')
      .then(r => setInquiries(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const filtered = inquiries.filter(i => {
    if (filter === 'animal')  return i.type !== 'meat';
    if (filter === 'meat')    return i.type === 'meat';
    if (filter === 'open')    return i.status === 'open';
    if (filter === 'replied') return i.status === 'replied';
    return true;
  });

  const counts = {
    all:     inquiries.length,
    animal:  inquiries.filter(i => i.type !== 'meat').length,
    meat:    inquiries.filter(i => i.type === 'meat').length,
    open:    inquiries.filter(i => i.status === 'open').length,
    replied: inquiries.filter(i => i.status === 'replied').length,
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">My Inquiries</h2>
        <p className="text-sm text-secondary mt-1">
          Track all messages you've sent to farmers and butchers.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all',     label: `All (${counts.all})`           },
          { key: 'animal',  label: `🐐 Animals (${counts.animal})` },
          { key: 'meat',    label: `🥩 Meat (${counts.meat})`      },
          { key: 'open',    label: `Open (${counts.open})`         },
          { key: 'replied', label: `Replied (${counts.replied})`   },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filter === f.key
                ? 'bg-primary text-white'
                : 'bg-white border border-outline-variant/20 text-secondary hover:bg-surface-container'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">forum</span>
          <p className="mt-3 font-bold text-on-surface">No inquiries yet</p>
          <p className="text-xs text-secondary mt-1">
            Your messages to farmers and butchers will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(inq => (
            <InquiryCard key={inq.id} inquiry={inq} />
          ))}
        </div>
      )}
    </div>
  );
}